
import { useNavigate } from 'react-router-dom';

interface BlogCardProps {
  article: {
    slug: string;
    title: string;
    excerpt: string;
    image: string;
    author: string;
    date: string;
    readTime: string;
    category: string;
    city: string;
    state: string;
  };
  featured?: boolean;
}

const BlogCard = ({ article, featured = false }: BlogCardProps) => {
  const navigate = useNavigate();

  if (featured) {
    return (
      <article
        onClick={() => navigate(`/blog/${article.slug}`)}
        className="group relative rounded-2xl overflow-hidden cursor-pointer h-[420px]"
      >
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={`${article.title} - tax tips for ${article.city}`}
            title={`${article.title} - ${article.city} tax guide`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            width="800"
            height="450"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <span className="inline-block px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full mb-3 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-teal-300 transition-colors leading-tight">
            {article.title}
          </h3>
          <p className="text-gray-300 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      onClick={() => navigate(`/blog/${article.slug}`)}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
    >
      <div className="relative w-full h-[200px] overflow-hidden">
        <img
          src={article.image}
          alt={`${article.title} - tax tips for ${article.city}`}
          title={`${article.title} - ${article.city} tax guide`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          width="800"
          height="450"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full whitespace-nowrap">
            {article.city}, {article.state}
          </span>
        </div>
      </div>
      <div className="p-5">
        <span className="inline-block px-2.5 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full mb-3 whitespace-nowrap">
          {article.category}
        </span>
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
          {article.title}
        </h3>
        <p className="text-gray-500 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{article.author}</span>
          <div className="flex items-center gap-2">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
